import { sum, isEqual, omit, sortBy } from "lodash";
import { Model } from "@vuex-orm/core";
import type { Collections, Fields, Query, Record } from "@vuex-orm/core";
import type { Config, Response } from "@vuex-orm/plugin-axios";
import type Mutators from "@vuex-orm/core/dist/src/attributes/contracts/Mutators";
import type { ActionType } from "@modules/event_processor/types";
import type { eventWithTime } from "@rrweb/types";
import { type Song } from "@modules/songs";
import { type playerConfig } from "rrweb";
import { formatSecondsAsString } from "../modules/helpers";

// eslint-disable-next-line import/no-cycle
import { Action, BrowserAction, BrowserActionTypes } from "./ActionHierarchy";
import type { ActionJson } from "./ActionHierarchy";
// eslint-disable-next-line import/no-cycle
import VoiceOver from "./VoiceOver";
// eslint-disable-next-line import/no-cycle
import AudioRecording from "./AudioRecording";
// eslint-disable-next-line import/no-cycle
import Recording from "./Recording";
import Publication from "./Publication";

export type Language = {
  code: string;
  name: string;
};

export interface VideoJson {
  id: number;
  token: string | null;
  name: string | null;
  // "new" means "unsaved"
  status:
    | "new"
    | "saved"
    // @deprecated
    | "draft"
    | "unpublished"
    | "activated"
    | "published";
  initialUrl: string | null;
  height: number;
  enableTranslations: boolean;
  primaryLanguage: string | null;
  languages?: Language[];
  backgroundSongId: number | null;
  backgroundMusicVolumeDuringSilence: number;
  backgroundMusicVolumeDuringVoiceOver: number;
  logoUrl: string | null;
  actions: ActionJson[];
  deletedActionIds?: string[];
  // FIXME: the following aren't typed as 'simple' JSON objects
  recordings: Recording[];
  voiceOvers: VoiceOver[];
  voiceKey: string;
  audioRecordings: AudioRecording[];
  // end of FIXME
  url: string;
  baseFileUrl: string;
  eventsUrl: string;
  publicationsUrl: string;
  thumbnailUrl: string | null;
  shareUrl: string;
  guideUrl: string;
  embedUrl: string;
  embedHtml: string;
  privateEmbedHtml: string;
  privateGuideUrl: string;
  updatedAt: string;
}

export default class Video extends Model implements VideoJson {
  // This is the name used as module name of the Vuex Store.
  public static entity = "videos";

  public static get apiConfig(): Config {
    return {
      // headers: { 'X-Requested-With': 'XMLHttpRequest' },
      baseURL: "/api/v1/videos",
      // actions: {
      //   fetchById(id) {
      //     return this.get(id.toString());
      //   },
      // },
    };
  }

  // List of all fields (schema) of the post model. `this.attr` is used
  // for the generic field type. The argument is the default value.
  public static fields(): Fields {
    return {
      id: this.number(-1),
      token: this.attr(null),
      name: this.attr(null),
      status: this.string("new"), // "new" means "unsaved", really
      creatorName: this.string(""),
      initialUrl: this.attr(null),
      height: this.number(720),
      enableTranslations: this.boolean(false),
      primaryLanguage: this.attr(null),
      languages: this.attr(null),
      backgroundSongId: this.number(null).nullable(),
      backgroundSong: this.attr(null).nullable(), // Only present in the public video API
      backgroundMusicVolumeDuringSilence: this.number(0.6),
      backgroundMusicVolumeDuringVoiceOver: this.number(0.1),
      logoUrl: this.attr(null),
      actions: this.hasMany(Action, "videoId"),
      deletedActionIds: this.attr([]),
      recordings: this.hasMany(Recording, "videoId"),
      totalRecordingTime: this.number(null).nullable(),
      voiceOvers: this.hasMany(VoiceOver, "videoId"),
      voiceKey: this.string(""),
      audioRecordings: this.hasMany(AudioRecording, "videoId"),
      publications: this.hasMany(Publication, "videoId"),
      // actions: this.actions.map((action) => this.formatActionForBackend(action)),
      // voiceOvers: this.voiceOvers.map((voiceOver) => (
      //   this.formatVoiceOverForBackend(voiceOver)
      // )),
      url: this.string(""),
      baseFileUrl: this.string(""),
      eventsUrl: this.string(""),
      publicationsUrl: this.string(""),
      thumbnailUrl: this.attr(null).nullable(),
      shareUrl: this.string(""),
      guideUrl: this.string(""),
      embedUrl: this.string(""),
      embedHtml: this.string(""),
      privateEmbedHtml: this.string(""),
      privateGuideUrl: this.string(""),
      createdAt: this.attr(""),
      updatedAt: this.attr(""),
      showBranding: this.boolean(true),
      customRrwebPlayerConfig: this.attr({}), // Only present in the public video API
      // events: this.attr([]),
    };
  }

  public declare id: number;

  public declare token: string | null;

  public declare name: string | null;

  // "new" means "unsaved"
  // "draft" is legacy and should not be used
  public declare status: "new" | "saved" | "draft" | "unpublished" | "activated" | "published";

  public declare creatorName: string;

  public declare initialUrl: string | null;

  public declare height: number;

  public declare enableTranslations: boolean;

  public declare primaryLanguage: string | null;

  public declare languages: Language[];

  public declare backgroundSongId: number | null;

  public declare backgroundMusicVolumeDuringSilence: number;

  public declare backgroundMusicVolumeDuringVoiceOver: number;

  public declare logoUrl: string | null;

  public declare actions: Action[];

  public declare deletedActionIds: string[];

  public declare recordings: Recording[];

  public declare totalRecordingTime: number | null;

  public declare voiceOvers: VoiceOver[];

  public declare voiceKey: string;

  public declare audioRecordings: AudioRecording[];

  public declare publications: Publication[];

  public declare url: string;

  public declare baseFileUrl: string;

  public declare eventsUrl: string;

  public declare publicationsUrl: string;

  public declare thumbnailUrl: string | null;

  public declare shareUrl: string;

  public declare guideUrl: string;

  public declare embedUrl: string;

  public declare embedHtml: string;

  public declare privateEmbedHtml: string;

  public declare privateGuideUrl: string;

  public declare updatedAt: string;

  public declare createdAt: string;

  public declare showBranding: boolean;

  public static mutators(): Mutators {
    return {
      // id(value) {
      //   return value ? id.toString() : null;
      // },
      initialUrl(value): string {
        if (value && value.toString().length > 3) {
          if (value.match(/https?:\/\//)) return value;
          return `http://${value}`;
        }
        return value;
      },
    };
  }

  public static async createNewOnServer(attributes?: Partial<VideoJson>): Promise<Video> {
    const result = await this.api().post("", { video: { name: "Untitled", ...attributes } });
    const videoFromResponse = result?.entities?.videos?.[0] as VideoJson | undefined;
    if (!videoFromResponse || !videoFromResponse.id)
      throw new Error("Video could not be created on the server");
    return Video.find(videoFromResponse.id) as Video;
  }

  public static async fetch(id: string, language?: string): Promise<Video> {
    // Provide a token instead of an id when on the public API.
    const path = language ? `${id}?language=${language}` : id.toString();
    const result = await this.api().get(path);
    const videoFromResponse = result.entities?.videos?.[0] as VideoJson | undefined;
    if (!videoFromResponse || !videoFromResponse.id)
      throw new Error(`No video found with id of ${id}`);
    return Video.find(videoFromResponse.id) as Video;
  }

  public toJsonForApi(
    actions: Action[],
    voiceOvers: VoiceOver[],
    action?: "publish" | "unpublish",
  ): Record {
    const result = this.$getAttributes();
    if (action) {
      result.action = action; // this is the action that the Video on the server should perform.
    }
    // check if this video already had it's action and voiceOver relationships included
    if (result.actions.length !== actions.length) {
      result.actions = actions?.map((a) => a.$getAttributes());
    }
    if (result.voiceOvers.length !== voiceOvers.length) {
      result.voiceOvers = sortBy(
        voiceOvers?.map((voiceOver) => voiceOver.$getAttributes()) || [],
        "id",
      );
    }
    return result;
  }

  public async saveToApi(
    actions: Action[],
    voiceOvers: VoiceOver[],
    action?: "publish" | "unpublish",
  ): Promise<void> {
    const json = this.toJsonForApi(actions, voiceOvers, action);
    const result = await Video.api().patch(json.url, { video: json });
    const videoFromResponse = result?.entities?.videos?.[0] as VideoJson | undefined;
    if (!videoFromResponse) throw new Error(`Video could not be saved to the server`);
    await Video.update({
      where: videoFromResponse.id,
      data: {
        id: videoFromResponse.id,
        updatedAt: videoFromResponse.updatedAt,
        status: videoFromResponse.status,
      },
    });
    // console.info("save result", result);
  }

  public async destroy(actions: Action[], voiceOvers: VoiceOver[]): Promise<void> {
    if (this.status !== "new") {
      await this.saveToApi(actions, voiceOvers);
      await Video.api().delete(this.url);
    }

    await Action.delete((action) => action.videoId === this.id);
    await Recording.delete((recording) => recording.videoId === this.id);
    await AudioRecording.delete((audioRecording) => audioRecording.videoId === this.id);
    await Video.delete(this.id);
  }

  public async saveToApiAsNewDraft(actions: Action[], voiceOvers: VoiceOver[]): Promise<Video> {
    const json = {
      ...this.toJsonForApi(actions, voiceOvers),
      id: null,
      status: "unpublished",
    };
    const result = await Video.api().post("", { video: json });
    // TODO: verify result?.entities?.videos[0] is instance of Video
    const videoFromResponse = result?.entities?.videos?.[0] as VideoJson | undefined;
    if (!videoFromResponse) throw new Error(`Video could not be created on the server`);
    return Video.find(videoFromResponse.id) as Video;
  }

  public get width(): number {
    return this.height * this.aspectRatio;
  }

  // eslint-disable-next-line class-methods-use-this
  public get aspectRatio(): number {
    return 16 / 9;
  }

  public get actionIds(): string[] {
    return this.actions.map((action) => action.id);
  }

  public get voiceOverIds(): string[] {
    return this.voiceOvers.map((voiceOver) => voiceOver.id);
  }

  public get isPublished(): boolean {
    return this.status === "published";
  }

  public fileUrl(fileKey: string): string {
    return Video.fileUrl(this.baseFileUrl, fileKey);
  }

  public static fileUrl(baseFileUrl: string, fileKey: string): string {
    return `${baseFileUrl}/${fileKey}`;
  }

  public proxiedFileUrl(fileKey: string): string {
    return `${this.fileUrl(fileKey)}/proxy`;
  }

  // public get keyframes() {
  //   return this.actions.map((action, index) => Keyframe.new({
  //     id: action.id,
  //     index,
  //     action,
  //     nextActionId: this.actions[ index+1 ]?.id,
  //   }));
  // }

  // voiceOverKeyframes() { // this does not appear to add any value when we can use getters, which then simply be handle within VoiceOver only.
  //   return this.voiceOvers.map((voiceOver, index) => {
  //     // const audioRecording = voiceOver.audioRecordings[0];

  //     return {
  //       voiceOver, // √
  //       // index, // √ => disabled because we don’t appear to use it anywhere.
  //       // duration, // √ getter: VoiceOverKeyframe#duration
  //       // length: voiceOver.length, // √ getter: VoiceOverKeyframe#length
  //       // actionIds, // √ getter in VoiceOverKeyframe
  //       // totalDurationOfVisibleActions, // √ getter in VoiceOverKeyframe
  //       // selectedAudioRecording, // √ getter in VoiceOverKeyframe
  //       // waitAfterLastAction: Math.max(0, duration - totalDurationOfActions), // √ getter
  //       // FIXME: This is confusing here because it’s expressed in frames instead of seconds:
  //       // We assume a duration of 1 second equals one frame length:
  //       // portion: Math.min(duration / voiceOver.length, 1.0),
  //     };
  //   });
  // }
}

export type PublicVideo = Video & {
  backgroundSong: Song | null;
  customRrwebPlayerConfig: Partial<playerConfig>;
};
